import React, { useEffect, useMemo, useState } from 'react';
import {
  Bug, X, Camera, CheckCircle, Mic,
  Database, Calculator, AlertTriangle, XCircle, Timer, Sparkles, MoreHorizontal,
} from 'lucide-react';
import { useDictation, appendSpoken } from '../../utils/issueReporter/useDictation';
import {
  captureIssueContext,
  capturePageState,
  IssueContext,
} from '../../utils/issueReporter/issueCapture';
import { snapshotConsole, ConsoleEntry } from '../../utils/issueReporter/consoleBuffer';
import { snapshotNetwork, NetworkEntry } from '../../utils/issueReporter/networkBuffer';
import { ScreenshotResult } from '../../utils/issueReporter/screenshotCapture';
import { supportApi } from '../../utils/issueReporter/supportApi';

import { RubyGemMark } from '../RubyGemMark';
import { Z_HELP } from './ReportIssueButton';
interface Props {
  open: boolean;
  onClose: () => void;
  user: { id: string; email: string; role: string; name?: string };
  tenantId: string;
  tenantName?: string;
  productId: string;
  /** Captured *before* this modal renders so the modal isn't visible in the
   * shot. Null = no screenshot attached. */
  screenshot: ScreenshotResult | null;
  onSubmitted?: (issueId: string, ticketNumber: string) => void;
}

// One-tap issue types. `value` maps to the AppIssueCategory backend enum.
const ISSUE_TYPES: Array<{ value: string; label: string; Icon: React.ComponentType<any> }> = [
  { value: 'data_issue', label: 'Wrong Data', Icon: Database },
  { value: 'calculation', label: 'Wrong Calculation', Icon: Calculator },
  { value: 'crash', label: 'Error / Crash', Icon: AlertTriangle },
  { value: 'broken_feature', label: 'Not Working', Icon: XCircle },
  { value: 'ui_bug', label: 'UI Glitch', Icon: Bug },
  { value: 'performance', label: 'Slow', Icon: Timer },
  { value: 'feature_request', label: 'New Feature', Icon: Sparkles },
  { value: 'other', label: 'Other', Icon: MoreHorizontal },
];

const PRIORITIES: Array<{ value: 'minor' | 'annoying' | 'blocking' | 'critical'; label: string }> = [
  { value: 'minor', label: 'Minor' },
  { value: 'annoying', label: 'Annoying' },
  { value: 'blocking', label: 'Blocking' },
  { value: 'critical', label: 'Critical' },
];

const RED = '#e5484d';
const RED_HOVER = '#f15a5f';
const NEON = '#2bff88';

const ReportIssueModal: React.FC<Props> = ({
  open,
  onClose,
  user,
  tenantId,
  tenantName,
  productId,
  screenshot,
  onSubmitted,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('ui_bug');
  const [priority, setPriority] = useState<'minor' | 'annoying' | 'blocking' | 'critical'>('annoying');
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);

  // Snapshots captured at modal-open time.
  const [context, setContext] = useState<IssueContext | null>(null);
  const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);
  const [networkEntries, setNetworkEntries] = useState<NetworkEntry[]>([]);
  const [pageState, setPageState] = useState<string | undefined>(undefined);

  const titleDictation = useDictation((t) => setTitle((p) => appendSpoken(p, t)));
  const descDictation = useDictation((t) => setDescription((p) => appendSpoken(p, t)));
  useEffect(() => {
    if (!open) { titleDictation.stop(); descDictation.stop(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setCategory('ui_bug');
    setPriority('annoying');
    setNotify(true);
    setSubmitting(false);
    setError(null);
    setSuccessId(null);

    setContext(
      captureIssueContext({
        userId: user.id,
        userEmail: user.email,
        userRole: user.role,
        tenantId,
        tenantName,
        productId,
      }),
    );
    setConsoleEntries(snapshotConsole(25));
    setNetworkEntries(snapshotNetwork(10));
    setPageState(capturePageState());
  }, [open, user.id, user.email, user.role, tenantId, tenantName, productId]);

  const canSubmit = useMemo(
    () => title.trim().length >= 3 && description.trim().length > 0 && !submitting,
    [title, description, submitting],
  );

  const errorCount = consoleEntries.filter((c) => c.level === 'error').length;
  const failedCalls = networkEntries.filter((n) => n.status === 0 || n.status >= 400);

  const handleSubmit = async () => {
    if (!canSubmit || !context) return;
    setSubmitting(true);
    setError(null);
    try {
      let screenshotKey: string | undefined;
      if (screenshot) {
        screenshotKey = await supportApi.uploadScreenshot(screenshot.blob);
      }
      const res = await supportApi.createIssue({
        title: title.trim(),
        description: description.trim(),
        category,
        priority: priority as any,
        context,
        consoleErrors: consoleEntries,
        networkFailures: failedCalls,
        pageState,
        screenshotKey,
        notifyByEmail: notify,
      });
      setSuccessId(res.issue?.id || null);
      onSubmitted?.(res.issue?.id, res.ticketNumber);
    } catch (e: any) {
      setError(e?.message || 'Failed to submit issue');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const MicButton: React.FC<{ d: ReturnType<typeof useDictation>; inTextarea?: boolean }> = ({ d, inTextarea }) =>
    !d.supported ? null : (
      <button
        type="button"
        onClick={d.toggle}
        title={d.listening ? 'Stop dictation' : 'Dictate'}
        aria-label={d.listening ? 'Stop dictation' : 'Dictate'}
        className={`flex-none rounded-full border flex items-center justify-center transition-all ${
          inTextarea ? 'absolute top-2 right-2 w-11 h-11' : 'w-[46px] h-[46px]'
        } ${d.listening ? 'animate-pulse' : ''}`}
        style={{
          background: d.listening ? '#04140b' : '#000',
          borderColor: d.listening ? NEON : 'rgba(0,0,0,.14)',
          color: d.listening ? '#5fffa8' : NEON,
        }}
      >
        <Mic size={18} style={{ filter: 'drop-shadow(0 0 5px rgba(43,255,136,.85))' }} />
      </button>
    );

  return (
    <div style={{ zIndex: Z_HELP + 3 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div
        className="w-full max-w-4xl my-8 rounded-[20px] overflow-hidden bg-white"
        style={{ border: '1px solid rgba(0,0,0,0.10)', boxShadow: '0 30px 80px rgba(0,0,0,0.22)' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3.5 px-6 py-4" style={{ background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <div className="w-11 h-11 flex-none rounded-xl flex items-center justify-center text-white" style={{ background: RED }}>
            <Bug size={22} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[20px] font-bold tracking-[-0.3px]" style={{ color: '#141414' }}>Report an issue</div>
            <div className="text-[13px] mt-0.5" style={{ color: '#6b6b6b' }}>We'll attach the page &amp; screenshot automatically</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex-none rounded-lg flex items-center justify-center text-neutral-400 hover:bg-black/5 hover:text-neutral-900" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {successId ? (
          <div className="p-10 text-center">
            <CheckCircle size={52} className="mx-auto text-emerald-500 mb-4" />
            <div className="text-lg font-semibold mb-1" style={{ color: '#141414' }}>Thanks — we got it.</div>
            <div className="text-sm mb-6" style={{ color: '#6b6b6b' }}>
              Your report is in the queue. You'll get an email when there's an update.
            </div>
            <div className="flex items-center justify-center">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-white font-semibold text-sm" style={{ background: RED }}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 pt-[18px] pb-6">
            {/* Auto-captured strip */}
            <div className="flex flex-wrap items-center justify-between gap-x-[18px] gap-y-2.5 rounded-xl px-[15px] py-[11px] mb-[22px]" style={{ background: '#f6f6f6', border: '1px solid rgba(0,0,0,0.08)' }}>
              <div className="flex items-center gap-2.5 flex-wrap text-[13px]" style={{ color: '#555' }}>
                <span className="text-[10px] font-bold tracking-[1.3px]" style={{ color: RED }}>AUTO-CAPTURED</span>
                <span>
                  <b style={{ color: '#141414', fontWeight: 600 }}>{context?.pageTitle || 'This page'}</b>
                  {' · '}
                  <b className="font-mono" style={{ color: '#333' }}>{context?.route || '—'}</b>
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Chip ok icon={<Camera size={13} />}>{screenshot ? 'Screenshot ready' : 'No screenshot'}</Chip>
                <Chip>{errorCount} error{errorCount === 1 ? '' : 's'}</Chip>
                <Chip>{failedCalls.length} failed call{failedCalls.length === 1 ? '' : 's'}</Chip>
              </div>
            </div>

            {/* Two columns */}
            <div className="flex flex-wrap gap-x-[30px] gap-y-6">
              {/* LEFT: type + severity */}
              <div className="flex-1 min-w-0" style={{ flexBasis: '340px' }}>
                <div className="text-[13px] font-semibold mb-2.5" style={{ color: '#141414' }}>
                  What kind of issue? <span style={{ color: RED }}>*</span>
                </div>
                <div className="grid gap-2 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                  {ISSUE_TYPES.map(({ value, label, Icon }) => {
                    const sel = category === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setCategory(value)}
                        className="flex items-center gap-2.5 px-3 py-[11px] rounded-xl transition-colors text-left"
                        style={{
                          background: sel ? 'rgba(229,72,77,.10)' : '#f4f4f5',
                          border: `1px solid ${sel ? RED : 'rgba(0,0,0,.10)'}`,
                        }}
                      >
                        <span className="flex-none" style={{ color: RED }}><Icon size={20} strokeWidth={1.7} /></span>
                        <span className="text-[13.5px] font-semibold" style={{ color: sel ? '#141414' : '#333' }}>{label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="text-[13px] font-semibold mb-2.5" style={{ color: '#141414' }}>How urgent is it?</div>
                <div className="flex gap-1.5 rounded-xl p-[5px]" style={{ background: '#f4f4f5', border: '1px solid rgba(0,0,0,0.08)' }}>
                  {PRIORITIES.map((p) => {
                    const sel = priority === p.value;
                    return (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setPriority(p.value)}
                        className="flex-1 rounded-[9px] py-2.5 px-1 text-[12.5px] font-semibold transition-all"
                        style={{ background: sel ? RED : 'transparent', color: sel ? '#fff' : '#666' }}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: title + description */}
              <div className="flex-1 min-w-0" style={{ flexBasis: '460px' }}>
                <div className="text-[13px] font-semibold mb-2.5" style={{ color: '#141414' }}>
                  Title <span style={{ color: RED }}>*</span>
                </div>
                <div className="flex items-center gap-2.5 mb-[18px]">
                  <input
                    type="text"
                    value={titleDictation.listening && titleDictation.interim ? appendSpoken(title, titleDictation.interim) : title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={160}
                    placeholder="Short summary, e.g. 'Totals show wrong figure'"
                    className="flex-1 min-w-0 rounded-xl px-[15px] py-[13px] text-[15px] outline-none"
                    style={{ background: '#f4f4f5', border: '1px solid rgba(0,0,0,.12)', color: '#111' }}
                  />
                  <MicButton d={titleDictation} />
                </div>

                <div className="flex items-baseline justify-between mb-2.5">
                  <span className="text-[13px] font-semibold" style={{ color: '#141414' }}>
                    What went wrong? <span style={{ color: RED }}>*</span>
                  </span>
                  {(descDictation.listening || titleDictation.listening) && (
                    <span className="text-[12px] font-semibold" style={{ color: '#12b866' }}>● Listening…</span>
                  )}
                </div>
                <div className="relative">
                  <textarea
                    rows={5}
                    value={descDictation.listening && descDictation.interim ? appendSpoken(description, descDictation.interim) : description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={4000}
                    placeholder="Tell us what you expected and what actually happened…"
                    className="w-full rounded-xl pl-[15px] pr-[62px] py-[13px] text-[14px] leading-[1.5] outline-none resize-none"
                    style={{ background: '#f4f4f5', border: '1px solid rgba(0,0,0,.12)', color: '#111' }}
                  />
                  <MicButton d={descDictation} inTextarea />
                </div>
                <div className="text-[12px] mt-2" style={{ color: '#8a8a8a' }}>
                  Plain English is fine — or tap the mic to dictate.
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 rounded-lg px-3 py-2 text-sm" style={{ background: 'rgba(229,72,77,.08)', border: '1px solid rgba(229,72,77,.3)', color: '#a3282c' }}>
                {error}
              </div>
            )}

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3.5 mt-[22px] pt-[18px]" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <button type="button" onClick={() => setNotify((n) => !n)} className="flex items-center gap-2.5 text-[13px]" style={{ color: '#555' }}>
                <span className="w-5 h-5 flex-none rounded-md flex items-center justify-center" style={{ background: notify ? RED : 'transparent', border: `1px solid ${notify ? RED : 'rgba(0,0,0,.3)'}` }}>
                  {notify && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round"><path d="M5 12l5 5L20 6" /></svg>
                  )}
                </span>
                Email me on updates
              </button>
              <div className="flex gap-2.5 ml-auto">
                <button type="button" onClick={onClose} className="text-[14px] font-semibold px-5 py-[11px] rounded-[11px]" style={{ border: '1px solid rgba(0,0,0,.16)', color: '#333' }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.background = RED_HOVER; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = RED; }}
                  className="text-[14px] font-bold text-white px-6 py-[11px] rounded-[11px] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: RED, boxShadow: '0 6px 18px rgba(229,72,77,.3)' }}
                >
                  {submitting && <RubyGemMark size={15} drawing color="currentColor" />}
                  Submit report
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Chip: React.FC<{ ok?: boolean; icon?: React.ReactNode; children: React.ReactNode }> = ({ ok, icon, children }) => (
  <span
    className="inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-[5px] rounded-lg"
    style={{ color: ok ? '#333' : '#888', background: '#fff', border: '1px solid rgba(0,0,0,.10)' }}
  >
    {icon}
    {children}
  </span>
);

export default ReportIssueModal;
